import { debounce } from '../../../static/js/utils/debounce';

class Parallax {
  _state = {
    inViewport: false,
    imageSize: null,
    width: null,
  };

  static options = {
    speed: 0.1,
  };

  constructor(element, options) {
    this._element = element;
    this._options = { options, ...Parallax.options };
    this._cacheSelectors();
    this._initialize();
  }

  _cacheSelectors() {
    this._elements = {
      image: this._element.querySelector('.parallax__image'),
    };
  }

  _initialize() {
    const debounceTime = 300;

    window.addEventListener(
      'resize',
      debounce(() => {
        this._resize();
      }, debounceTime)
    );

    // Make sure to call this to set the initial state
    this._resize();
    this._moveParallax();

    // Create the intersection observer
    const parallaxObserver = new IntersectionObserver((entries, observer) =>
      this._viewportHandler(entries, observer)
    );

    // Start observing
    parallaxObserver.observe(this._elements.image);
  }

  /**
   * Determine if the parallax should be scrolling based
   * off the fact that it is inside of the viewport
   * @param {*} entries
   */
  _viewportHandler(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        window.addEventListener('scroll', this._moveParallax.bind(this));
      } else {
        window.removeEventListener('scroll', this._moveParallax.bind(this));
      }
    }
  }

  /**
   * Moving the parallax
   */
  _moveParallax() {
    const scrolled = window.scrollY;

    const bodyRect = document.body.getBoundingClientRect();
    const elemRect = this._element.getBoundingClientRect();
    const offset = elemRect.top - bodyRect.top;

    let coords = `center calc(100% + ${(scrolled - offset) * this._options.speed}px)`;

    const minParallaxWidth = 500;

    if (this._state.width < minParallaxWidth) {
      coords = `center center`;
    }

    this._elements.image.style.backgroundPosition = coords;
  }

  /**
   * Resize
   */
  _resize() {
    // Set correct image based on the screen size
    this._state.width =
      window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    const breakpointL = 1200;
    const breakpointXL = 1440;

    if (this._state.width > breakpointXL) {
      this._setImage('Xxl');
    } else if (this._state.width > breakpointL) {
      this._setImage('Xl');
    } else {
      this._setImage('M');
    }
  }

  /**
   * Set the background image based on the screen size
   * @param {*} size
   */
  _setImage(size) {
    const image = this._elements.image.dataset[`image${size}`];
    this._elements.image.style.backgroundImage = `url('${image}')`;
  }
}

export default Parallax;
